import React, { useEffect, useState } from "react";
import SuperUserHeader from "../../components/SuperUserHeader";
import SuperUserSideMenu from "../../components/SuperUserSideMenu";
import { useNavigate } from "react-router-dom";
import TextInput from "../../components/TextInput";
import CustomTextarea from "../../components/CustomTextarea";
import PasswordInput from "../../components/PasswordInput";
import CustomButton from "../../components/CustomButton";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import CustomToast from "../../components/CustomToast";
import { LoadingSpinner } from "../../components/Icons";
import { validatePassword, validateFieldReq, validateEmail } from "../../utils/validation";
import {
    createSuperUser,
    setSuccess,
    setError,
} from "../../reducers/superUser";
import ReturnButton from "../../components/ReturnButton";

const initialData = {
    first_name: "",
    last_name: "",
    email: "",
    note: "",
};

const initialErrorData = {
    first_name: "",
    last_name: "",
    email: "",
    note: "",
};

const UserCreation = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
    const [formData, setFormData] = useState(initialData);
    const [errors, setErrors] = useState(initialErrorData);
    const {
        loading,
        success,
        error,
        actionName,
        detailData,
    } = useSelector((state: any) => state.superUser);

    const validateFormData = () => {
        let formErrors = {
            first_name: "",
            last_name: "",
            email: "",
            note: "", 
        };

        const validateLastnameRes = validateFieldReq(formData.last_name, "姓");
        if(validateLastnameRes != true) {
            formErrors.last_name = String(validateLastnameRes);
        }

        const validateFirstnameRes = validateFieldReq(formData.first_name, "名");
        if(validateFirstnameRes != true) {
            formErrors.first_name = String(validateFirstnameRes);
        }
        
        const validateEmailRes = validateEmail(formData.email);
        if(validateEmailRes != true) {
            formErrors.email = String(validateEmailRes);
        }

        setErrors(formErrors);
        return !Object.values(formErrors).some((error) => error);
    };

    const handleSubmit = (e: any) => {
        e.preventDefault();
        if (validateFormData()) {
            const data  = {
                requestBody: {
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    email: formData.email,
                    note: formData.note,
                },
            }
            dispatch(createSuperUser(data));
        }
    };

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    useEffect(() => {
        if(error) {
            enqueueSnackbar("I love snacks.", {
                content: (key) => (
                <CustomToast
                    title={"メッセージ"}
                    message={error}
                    variant={"error"}
                    snackbarKey={key}
                    closeSnackbar={closeSnackbar}
                />
                ),
            });
            dispatch(setError(null));
        }
        else if(success) {
            if(actionName == "createSuperUser" && parseInt(detailData.id) > 0) {
                navigate("/control/user/detail/" + detailData.id);
            }
        }
    }, [error, success, actionName]);

    return (
        <div className="bg-alu-base h-dvh">
        <SuperUserHeader />
        <div className="mt-6 px-6 flex gap-6 relative">
            <SuperUserSideMenu />
            <div className="flex-1">
            <div className="mb-6 flex items-center gap-4">
                <ReturnButton onClick={() => navigate(-1)} />
                <span className="text-sm alu-text-base-1">/</span>
                <h3 className="text-xl leading-[1.875rem] alu-text-base-2 font-bold">
                    スーパーユーザーの新規登録
                </h3>
            </div>

            <div className="bg-white rounded-lg p-4">
                <form className="w-full">
                    <div className="w-[50%] max-w-[464px]">
                        <div className="grid grid-cols-[124px_1fr] items-center gap-4 mb-4">
                            <label className="flex whitespace-nowrap text-sm font-bold alu-text-base-2">
                                名前 <span className="alu-text-error">*</span>
                            </label>
                            <div className="flex items-center gap-4">
                                <div className="w-full">
                                    <TextInput
                                        name="last_name"
                                        placeholder="姓"
                                        value={formData.last_name}
                                        onChange={handleChange}
                                        error={errors.last_name}
                                        errorText={errors.last_name}
                                        required
                                    />
                                </div>
                                <div className="w-full">
                                    <TextInput
                                        name="first_name"
                                        placeholder="名"
                                        value={formData.first_name}
                                        onChange={handleChange}
                                        error={errors.first_name}
                                        errorText={errors.first_name}
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-[124px_1fr] items-center gap-4 mb-4">
                            <label className="flex whitespace-nowrap text-sm font-bold alu-text-base-2">
                                メールアドレス <span className="alu-text-error">*</span>
                            </label>
                            <div className="w-full">
                                <TextInput
                                    error={errors.email}
                                    errorText={errors.email}
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="入力してください"
                                    required
                                />
                            </div>
                        </div>
                        <div className="inline-block w-full mb-4">
                            <label className="block mb-2 whitespace-nowrap text-sm font-bold alu-text-base-2">
                                備考
                            </label>
                            <div className="w-full">
                                <CustomTextarea
                                    rows={4}
                                    name="note"
                                    placeholder="入力してください"
                                    value={formData.note}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                        <div className="w-full mt-6 grid grid-cols-2 items-center gap-4">
                            <CustomButton
                                type="button"
                                size="md"
                                width="full"
                                variant="outline"
                                onClick={() => navigate(-1)}
                            >
                                戻る
                            </CustomButton>
                            <CustomButton
                                type="button"
                                size="md"
                                width="full"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                <p>{loading ? "保存中..." : "登録"}</p>
                                {loading && (
                                    <span><LoadingSpinner /></span>
                                )}
                            </CustomButton>
                        </div>
                    </div>
                </form>
            </div>
            </div>
        </div>
        </div>
    );
};

export default UserCreation;
